import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import InquiryForm from "../components/InquiryForm";
import ReviewsSection from "../components/ReviewsSection";
import LearningPathway from "../components/LearningPathway";
import YoungEngineersCredibility from "../components/YoungEngineersCredibility";
import ProgramFAQ from "../components/ProgramFAQ";
import TryRiskFreeCTA from "../components/TryRiskFreeCTA";
import ProgramInfoBlocks from "../components/ProgramInfoBlocks";
import ProgramPathwayTracks from "../components/ProgramPathwayTracks";
import ProgramLogo from "../components/ProgramLogo";
import { ProgramArt } from "../components/ProgramCard";
import { fetchSiteProgram } from "../lib/api";

const SUPPORTED_SLUGS = new Set([
  "stem-engineering",
  "lego-engineering",
  "robotics-coding",
  "summer-camps",
  "tennis-programs",
  "kids-activities-richmond",
]);

// Map each program slug → which Young Engineers pathway stages to highlight.
const PATHWAY_HIGHLIGHT = {
  "stem-engineering": new Set(["Brick Challenge", "Galileo Technic"]),
  "lego-engineering": new Set(["Brick Challenge", "Galileo Technic"]),
  "robotics-coding": new Set(["AlgoPlay", "RoboToys"]),
};

export default function ProgramLandingPage() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchSiteProgram(slug)
      .then(setProgram)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (slug === "play-based-support") {
    return <Navigate to="/play-based-support" replace />;
  }
  if (!loading && (notFound || !program)) {
    return <Navigate to="/programs" replace />;
  }
  if (!loading && !SUPPORTED_SLUGS.has(slug)) {
    return <Navigate to="/programs" replace />;
  }

  if (loading || !program) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="text-slate-500 text-sm">Loading program…</p>
      </section>
    );
  }

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Programs", url: "/programs" },
    { name: program.name, url: `/programs/${program.slug}` },
  ];
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item:
        typeof window !== "undefined"
          ? window.location.origin + b.url
          : b.url,
    })),
  };

  const isWaitlist = program.program_type === "waitlist";
  const heroH1 = program.hero_title || program.name;
  const pathwayHighlight = PATHWAY_HIGHLIGHT[program.slug] || new Set();
  const hasPathway = (program.pathway || []).length > 0;
  const hasFaqs = (program.faqs || []).length > 0;
  const accent = program.accent_color || "#0369A1";
  const secondary = program.secondary_color || "#E0F2FE";

  return (
    <>
      <SEO
        path={`/programs/${program.slug}`}
        title={program.seo_title || heroH1}
        description={
          program.seo_description ||
          program.short_description ||
          program.description
        }
        image={program.image_url}
        program={program}
        faqs={program.faqs}
        schemas={[breadcrumbSchema]}
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: secondary }}
        data-testid={`program-hero-${program.slug}`}
      >
        <span
          className="absolute top-0 left-0 right-0 h-1.5"
          style={{ backgroundColor: accent }}
        />
        <div
          className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full blur-[120px] opacity-50 pointer-events-none"
          style={{ backgroundColor: accent }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-12 lg:pb-16">
          <nav className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <span>/</span>
            <Link to="/programs" className="hover:text-slate-700">Programs</Link>
            <span>/</span>
            <span className="text-slate-700 font-semibold">{program.name}</span>
          </nav>

          <div className="mt-6 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <ProgramLogo program={program} size={56} />
                <div>
                  <span
                    className="pill"
                    style={{ backgroundColor: "#FFFFFF", color: accent }}
                  >
                    {program.category}
                  </span>
                  {program.brand_label && (
                    <p className="mt-1.5 text-xs uppercase tracking-wider font-bold text-slate-500">
                      {program.brand_label}
                    </p>
                  )}
                </div>
              </div>
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-slate-900 mt-5 leading-[1.04]"
                data-testid="program-headline"
              >
                {heroH1}
              </h1>
              <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
                {program.hero_subtitle ||
                  program.short_description ||
                  program.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                {isWaitlist ? (
                  <a
                    href="#inquire"
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-opacity hover:opacity-90"
                    style={{ backgroundColor: accent }}
                    data-testid="program-hero-waitlist-cta"
                  >
                    Join the waitlist
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                ) : (
                  <Link
                    to="/trial-class"
                    state={{ programSlug: program.slug }}
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-opacity hover:opacity-90"
                    style={{ backgroundColor: accent }}
                    data-testid="program-hero-trial-cta"
                  >
                    Book a trial class
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                )}
                {program.bookwhen_url ? (
                  <a
                    href={program.bookwhen_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-soft"
                    data-testid="program-hero-schedule-cta"
                  >
                    View schedule
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                ) : (
                  <a
                    href="#inquire"
                    className="btn-soft"
                    data-testid="program-hero-inquire-cta"
                  >
                    Ask a question
                  </a>
                )}
              </div>

              <ul className="mt-8 grid sm:grid-cols-2 gap-2.5 max-w-xl">
                {(program.highlights || []).slice(0, 4).map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-3 text-sm text-slate-700"
                  >
                    <span
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: "#FFFFFF" }}
                    >
                      <Check className="h-3 w-3" style={{ color: accent }} />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden border border-slate-200/70 bg-white shadow-md">
                <ProgramArt program={program} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info blocks */}
      <ProgramInfoBlocks program={program} />

      {/* Program Overview + Learning Outcomes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-12">
        <article className="lg:col-span-7" data-testid="program-overview">
          <p className="eyebrow">Program Overview</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-3 leading-[1.08]">
            {program.name}
          </h2>
          {(program.full_description || program.description || "")
            .split("\n\n")
            .map((para, i) => (
              <p
                key={i}
                className="mt-5 text-slate-600 leading-relaxed whitespace-pre-line"
              >
                {para}
              </p>
            ))}

          {program.highlights && program.highlights.length > 0 && (
            <div className="mt-10">
              <p className="eyebrow text-slate-600">Inside every session</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                {program.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200/70 p-4"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-50">
                      <Check className="h-3.5 w-3.5 text-[#0369A1]" />
                    </span>
                    <span className="text-sm text-slate-700 leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>

        <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start grid gap-6">
          {(program.learning_outcomes || []).length > 0 && (
            <div
              className="rounded-[1.75rem] bg-white border border-slate-200/70 p-7"
              data-testid="program-outcomes"
            >
              <p className="eyebrow text-slate-600">Learning outcomes</p>
              <p className="mt-3 font-display font-bold text-slate-900">
                Students develop:
              </p>
              <ul className="mt-4 grid gap-2.5">
                {program.learning_outcomes.map((o) => (
                  <li
                    key={o}
                    className="flex items-start gap-3 text-sm text-slate-700"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-50">
                      <Check className="h-3 w-3 text-[#0369A1]" />
                    </span>
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {program.why_choose && program.why_choose.length > 0 && (
            <div
              className="rounded-[1.75rem] bg-slate-900 text-white p-7"
              data-testid="program-why"
            >
              <p className="eyebrow text-slate-400">Why families choose Nexora</p>
              <ul className="mt-5 grid gap-3 text-sm">
                {program.why_choose.map((w) => (
                  <li key={w} className="flex items-start gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10">
                      <Check className="h-3 w-3 text-[#FF8C42]" />
                    </span>
                    <span className="leading-relaxed">{w}</span>
                  </li>
                ))}
              </ul>
              {!isWaitlist && (
                <Link
                  to="/trial-class"
                  state={{ programSlug: program.slug }}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white text-slate-900 px-5 py-3 text-sm font-bold"
                  data-testid="program-side-trial-cta"
                >
                  Book a trial class
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          )}
        </aside>
      </section>

      {/* Program Pathway tracks */}
      {hasPathway && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <ProgramPathwayTracks tracks={program.pathway} />
        </section>
      )}

      {/* Young Engineers progression visual */}
      {pathwayHighlight.size > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <LearningPathway highlight={pathwayHighlight} />
        </section>
      )}

      {/* YE credibility */}
      {program.show_ye_credibility && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <YoungEngineersCredibility />
        </section>
      )}

      {/* Reviews */}
      <ReviewsSection programSlug={program.slug} />

      {/* FAQ */}
      {hasFaqs && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <ProgramFAQ
            faqs={program.faqs}
            subtitle="The questions parents ask us most about this program."
          />
        </section>
      )}

      {/* Try a Class Risk-Free CTA */}
      {!isWaitlist && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <TryRiskFreeCTA
            programSlug={program.slug}
            secondaryHref={program.bookwhen_url || undefined}
          />
        </section>
      )}

      {/* Inquiry */}
      <section
        id="inquire"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-10"
        data-testid={`program-inquire-${program.slug}`}
      >
        <div className="rounded-[2.5rem] bg-[#FFFCF7] border border-slate-200/70 p-8 sm:p-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">Get in touch</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 text-slate-900 leading-[1.08]">
              {isWaitlist
                ? "Join the waitlist for Nexora Tennis."
                : "Ask us anything — or just book a trial."}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Tell us your child's age and what you're hoping to get out of {program.name}.
              Our team replies within one business day.
            </p>
            {!isWaitlist && (
              <Link
                to="/trial-class"
                state={{ programSlug: program.slug }}
                className="mt-6 btn-primary"
                data-testid="program-inquire-trial-cta"
              >
                Book a trial class instead
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </div>
          <div className="lg:col-span-7">
            <InquiryForm
              source={`program_landing_${program.slug}`}
              sourcePage={`/programs/${program.slug}`}
              title={isWaitlist ? "Waitlist sign-up" : "Quick inquiry"}
              subtitle="Required fields are marked with *"
              ctaLabel={isWaitlist ? "Join the waitlist" : "Request information"}
            />
          </div>
        </div>
      </section>
    </>
  );
}
